import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { Search, Heart, MapPin, Scissors, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/useAuth";
import { logoutUser, supabase } from '../../lib/supabase';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

// Lazy-loaded: AuthModal statically imports Login.jsx + Register.jsx, which
// were previously shipping in every page's main bundle since Header renders
// globally, regardless of whether the modal is ever opened.
const AuthModal = lazy(() => import("../auth/AuthModal"));

export default function Header() {
  const { t } = useTranslation('nav');
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [mobileSearchFocused, setMobileSearchFocused] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);
  const { currentUser, loading, setError, clearSession } = useAuth();
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const debounceRef = useRef(null);
  const mobileDebounceRef = useRef(null);
  const abortRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [mobileSearchResults, setMobileSearchResults] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [mobileLoadingSearch, setMobileLoadingSearch] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchFocused(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setMobileSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current);
      // abortRef holds whichever AbortController is currently in flight
      // (reassigned by fetchAutocomplete throughout the component's life),
      // not a DOM node ref — reading .current here at unmount is deliberate,
      // not a stale snapshot.
      if (abortRef.current) abortRef.current.abort(); // eslint-disable-line react-hooks/exhaustive-deps
    };
  }, []);

  useEffect(() => {
    if (searchFocused && currentUser && favorites.length === 0 && !searchQuery) {
      fetchFavorites();
    }
  }, [searchFocused, currentUser, searchQuery, favorites.length]);

  useEffect(() => {
    if (mobileSearchFocused && currentUser && favorites.length === 0 && !mobileSearchQuery) {
      fetchFavorites();
    }
  }, [mobileSearchFocused, currentUser, mobileSearchQuery, favorites.length]);

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults(null);
      setLoadingSearch(false);
      return;
    }
    setLoadingSearch(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
 fetchAutocomplete(searchQuery.trim(), setSearchResults, setLoadingSearch, abortRef);    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchQuery]);

  useEffect(() => {
    if (mobileSearchQuery.trim().length < 2) {
      setMobileSearchResults(null);
      setMobileLoadingSearch(false);
      return;
    }
    setMobileLoadingSearch(true);
    if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current);
    mobileDebounceRef.current = setTimeout(() => {
      fetchAutocomplete(mobileSearchQuery.trim(), setMobileSearchResults, setMobileLoadingSearch, mobileAbortRef);
    }, 300);
    return () => { if (mobileDebounceRef.current) clearTimeout(mobileDebounceRef.current); };
  }, [mobileSearchQuery]);

  const mobileAbortRef = useRef(null);

  const fetchAutocomplete = async (term, setResults, setLoading, abortRefToUse) => {
    if (abortRefToUse.current) abortRefToUse.current.abort();
    const controller = new AbortController();
    abortRefToUse.current = controller;    try {
      const response = await fetch(
        `${import.meta.env.VITE_REACT_APP_API_URL}/api/search/autocomplete?q=${encodeURIComponent(term)}`,
        { signal: controller.signal }
      );
      const data = await response.json();
      if (data.success) setResults(data.data);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Autocomplete error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      setLoadingFavorites(true);
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) return;
      const response = await fetch(
        `${import.meta.env.VITE_REACT_APP_API_URL}/api/user/favourites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      if (data.success) setFavorites(data.data.slice(0, 5));
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  const handleLogout = async () => {
    try {
      navigate("/");
      setOpen(false);
      clearSession();
      await logoutUser();
    } catch (err) {
      console.error('Logout error:', err);
      setError(err);
    }
  };

  const renderSearchDropdown = ({ focused, query, loading: isLoading, results, onNavigate }) => {
    const showSearch = query.trim().length >= 2 && focused;
    const showFavs = !query && focused && currentUser;
    const hasResults = results &&
      (results.salons.length > 0 || results.employees.length > 0 || results.procedures.length > 0);

    const getInitials = (name) => {
      if (!name) return '?';
      return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
    };

    if (!showSearch && !showFavs) return null;

    return (
      <div className="absolute top-full mt-2 w-full bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50 max-h-[420px] overflow-y-auto">
        {showSearch && isLoading && (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-stone-700 mx-auto" />
          </div>
        )}
        {showSearch && !isLoading && hasResults && (
          <>
            {results.salons.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-stone-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">{t('salons')}</span>
                </div>
                {results.salons.map((salon) => (
                  <div key={`s-${salon.id}`} onClick={() => onNavigate(`/salon/${salon.id}`)}
                    className="px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0">
                    <div className="flex items-center gap-3">
                      {salon.image_url ? (
                        <img src={salon.image_url} alt={salon.salon_name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-stone-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate text-sm">{salon.salon_name}</h4>
                        <p className="text-xs text-gray-400 truncate">{[salon.address, salon.salon_city].filter(Boolean).join(', ')}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {results.employees.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-stone-50 border-b border-gray-100">
                    <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">{t('specialists')}</span>
                </div>
                {results.employees.map((emp) => (
                  <div key={`e-${emp.id}`} onClick={() => onNavigate(`/employee/${emp.id}`)}
                    className="px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0">
                    <div className="flex items-center gap-3">
                      {emp.photo_url ? (
                        <img src={emp.photo_url} alt={emp.display_name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-semibold text-stone-600">{getInitials(emp.display_name)}</span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate text-sm">{emp.display_name}</h4>
                        <p className="text-xs text-gray-400 truncate">{emp.salon_name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {results.procedures.length > 0 && (
              <div>
                <div className="px-4 py-2 bg-stone-50 border-b border-gray-100">
                  <span className="text-xs font-semibold text-stone-500 uppercase tracking-widest">{t('procedures_label')}</span>
                </div>
                {results.procedures.map((proc) => (
                  <div key={`p-${proc.id}`} onClick={() => onNavigate(`/salon/${proc.salon_id}`)}
                    className="px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
                        <Scissors className="w-5 h-5 text-rose-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate text-sm">{proc.title}</h4>
                        <p className="text-xs text-gray-400 truncate">{proc.service_name ? `${proc.service_name} · ` : ''}{proc.salon_name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {showSearch && !isLoading && !hasResults && results && (
          <div className="px-4 py-6 text-center text-gray-400 text-sm">
            <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p>{t('no_results')} „{query}"</p>
          </div>
        )}
        {showFavs && !showSearch && (
          <>
            {loadingFavorites ? (
              <div className="p-4 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-stone-700 mx-auto" />
              </div>
            ) : favorites.length > 0 ? (
              <div>
                <div className="px-4 py-2 bg-stone-50 border-b border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-stone-500 uppercase tracking-widest">
                    <Heart className="w-3.5 h-3.5 fill-rose-400 text-rose-400" />
                    <span>{t('favorite_salons')}</span>
                  </div>
                </div>
                {favorites.map((salon) => (
                  <div key={salon.id} onClick={() => onNavigate(`/salon/${salon.id}`)}
                    className="px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors border-b border-gray-50 last:border-b-0">
                    <div className="flex items-start gap-3">
                      {salon.image_url ? (
                        <img src={salon.image_url} alt={salon.salon_name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-stone-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate text-sm">{salon.salon_name}</h4>
                        <p className="text-xs text-gray-400 truncate">{salon.address}, {salon.salon_city}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">
                <Heart className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                <p>{t('no_favorites')}</p>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const navLinks = [
   { label: t('home'), href: "/" },
    { label: t('map'), href: "/map" },
    { label: t('for_salons'), href: "/addsalon" },
    { label: t('services'), href: "/about" },
  ];

   return (
    <>
  <style>{`
        /* Shared light-glass control surface. Sizes are driven by --hdr-h so the
           whole cluster scales down together on narrow viewports. */
        .hdr-ctl, .hdr-nav, .hdr-search {
          --hdr-h: 40px;
          -webkit-backdrop-filter: blur(14px) saturate(150%);
          backdrop-filter: blur(14px) saturate(150%);
          background: rgba(255,255,255,0.32);
          border: 1px solid rgba(255,255,255,0.85);
          box-shadow: 0 2px 10px rgba(24,38,45,0.08), 0 6px 22px rgba(24,38,45,0.06);
        }
        .hdr-ctl {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: var(--hdr-h);
          flex-shrink: 0;
          padding: 0;
          border-radius: 12px;
          color: var(--color-ink-900, #1a3a42);
          cursor: pointer;
          text-decoration: none;
          transition: background .2s, box-shadow .2s, transform .12s;
        }
        .hdr-ctl:hover {
          background: rgba(255,255,255,0.92);
          box-shadow: 0 3px 14px rgba(24,38,45,0.12), 0 8px 26px rgba(24,38,45,0.08);
        }
        .hdr-ctl:active { transform: scale(0.96); }

.hdr-logo-img {
  filter:
    drop-shadow(0 0 0.7px rgba(26, 58, 66, 0.35))
    drop-shadow(0 0 0.7px rgba(26, 58, 66, 0.35))
    drop-shadow(0 0 0.7px rgba(26, 58, 66, 0.25))
    drop-shadow(0 1px 3px rgba(26, 58, 66, 0.15));
}

        .hdr-btn { width: var(--hdr-h); flex-direction: column; gap: 5px; overflow: hidden; }
        .hdr-btn .bar {
          width: 18px;
          height: 2px;
          background: currentColor;
          border-radius: 100px;
          transition: 0.3s ease;
        }
        .hdr-btn.is-open .bar.top { transform: translateY(350%) rotate(45deg); width: 21px; }
        .hdr-btn.is-open .bar.bot { transform: translateY(-350%) rotate(-45deg); width: 21px; }
        .hdr-btn.is-open .bar.mid { transform: translateX(-26px); opacity: 0; }

        .hdr-lang { gap: 6px; padding: 0 8px 0 6px; }

        /* Primary CTA (login / account). Same glass surface as .hdr-ctl
           (translucent white, blur, same shadow), and the same fixed 40px
           height so it's actually level with the gallery button / nav pill
           — not just visually close. */
        .hdr-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          height: 40px;
          min-height: 40px;
          flex-shrink: 0;
          padding: 0 18px;
          border-radius: 12px;
          -webkit-backdrop-filter: blur(14px) saturate(150%);
          backdrop-filter: blur(14px) saturate(150%);
          background: rgba(255,255,255,0.32);
          border: 1px solid rgba(255,255,255,0.85);
          box-shadow: 0 2px 10px rgba(24,38,45,0.08), 0 6px 22px rgba(24,38,45,0.06);
          color: var(--color-ink-900, #1a3a42);
          font-size: 13.5px;
          font-weight: 700;
          letter-spacing: -0.01em;
          white-space: nowrap;
          cursor: pointer;
          transition: background .2s, box-shadow .2s, transform .12s;
        }
        .hdr-cta:hover {
          background: rgba(255,255,255,0.92);
          box-shadow: 0 3px 14px rgba(24,38,45,0.12), 0 8px 26px rgba(24,38,45,0.08);
        }
        .hdr-cta:active { transform: scale(0.96); }
        @media (max-width: 400px) {
          .hdr-cta { height: 36px; min-height: 36px; padding: 0 14px; border-radius: 10px; }
        }

       .hdr-nav {
          align-items: center;
          gap: 2px;
          height: var(--hdr-h);
          padding: 0 5px;
          border-radius: 14px;
        }
        .hdr-nav a {
          display: inline-flex;
          align-items: center;
          height: calc(var(--hdr-h) - 10px);
          padding: 0 14px;
          border-radius: 9px;
          font-size: 13.5px;
          font-weight: 600;
          letter-spacing: -0.01em;
          white-space: nowrap;
          text-decoration: none;
          color: var(--color-ink-700, #35606b);
          transition: color .18s, background .18s;
        }
        .hdr-nav a:hover {
          color: var(--color-primary-600, #1e6878);
          background: rgba(30,104,120,0.09);
        }

        .hdr-search {
          align-items: center;
          gap: 9px;
          height: var(--hdr-h);
          min-width: 0;
          padding: 0 14px;
          border-radius: 12px;
          transition: background .2s, box-shadow .2s;
        }
        .hdr-search:focus-within {
          background: rgba(255,255,255,0.95);
          box-shadow: 0 0 0 3px rgba(30,104,120,0.14), 0 6px 22px rgba(24,38,45,0.10);
        }
        .hdr-search input {
          flex: 1;
          min-width: 0;
          width: clamp(200px, 24vw, 380px);
          background: transparent;
          border: none;
          outline: none;
          color: var(--color-ink-900, #1a3a42);
          font-size: 13.5px;
          font-weight: 500;
          caret-color: var(--color-ink-900, #1a3a42);
        }
        .hdr-search input::placeholder { color: var(--color-ink-400, #8aa3ab); }

        @media (max-width: 1120px) {
          .hdr-nav a { padding: 0 11px; font-size: 13px; }
          .hdr-search input { width: clamp(140px, 16vw, 240px); }
        }
        @media (max-width: 400px) {
          .hdr-ctl { --hdr-h: 36px; border-radius: 10px; }
          .hdr-btn .bar { width: 16px; }
          .hdr-btn.is-open .bar.top,
          .hdr-btn.is-open .bar.bot { width: 19px; }
          .hdr-lang { gap: 4px; padding: 0 6px 0 5px; }
        }
      `}</style>

      <header role="banner" className="fixed top-0 left-0 right-0 z-40 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(255,255,255,0.85)" : "transparent",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(16px)" : "none",
          borderBottom: `1px solid ${scrolled ? "var(--color-border)" : "transparent"}`,
          paddingTop: "env(safe-area-inset-top)",
        }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex items-center justify-between gap-4 h-14">

            {/* Logo */}
            <div className="flex items-center cursor-pointer min-w-0 overflow-hidden" onClick={() => navigate("/")}>
              {/* Full logo — desktop */}
              <img
                src="/5.webp"
                alt="Salonite365"
              className="hdr-logo-img hidden lg:block"
                width={1429}
                height={163}
                style={{
                  height: "clamp(21px, 2.1vw, 30px)",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
              {/* Symbol only — mobile */}
              <img
                src="/5.webp"
                alt="Salonite365"
                className="hdr-logo-img lg:hidden"
                width={1429}
                height={163}
                style={{
                  height: "clamp(19px, 5.2vw, 26px)",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </div>

            {/* Desktop center nav */}
           <nav className="hdr-nav hidden lg:flex flex-shrink-0">
              {navLinks.map((link) => (
                <a key={link.label} href={link.href}>{link.label}</a>
              ))}
            </nav>

            {/* Desktop right actions */}
            <div className="hidden lg:flex items-center gap-2 flex-shrink min-w-0">
              {/* Search pill */}
               <div ref={searchRef} className="relative flex-shrink min-w-0">
                <div className="hdr-search flex">
                  <Search className="w-4 h-4 flex-shrink-0" style={{ color: "var(--color-ink-400)" }} />
                  <input
                    type="text"
                    placeholder={t('search_placeholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                  />
                </div>
                {renderSearchDropdown({
                  focused: searchFocused,
                  query: searchQuery,
                  loading: loadingSearch,
                  results: searchResults,
                  onNavigate: (path) => {
                    navigate(path);
                    setSearchFocused(false);
                    setSearchQuery("");
                    setSearchResults(null);
                  },
                })}
              </div>

               {/* Gallery */}
              <a href="/gallery" aria-label={t('gallery')} className="hdr-ctl hdr-btn">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="3.5" />
                  <circle cx="8" cy="8.2" r="1.5" />
                  <path d="M17 12.6l-3.1-3.1a2 2 0 0 0-2.8 0L3.7 16.9" />
                  <path d="M8 21h9a4 4 0 0 0 4-4V8" />
                </svg>
              </a>

               {/* Language switcher */}
              <div className="flex-shrink-0"><LanguageSwitcher compact /></div>

              {/* Auth */}
              {currentUser ? (
                <div className="relative flex-shrink-0">
                  <button onClick={() => setOpen(!open)} className="hdr-cta">
                    {currentUser.display_name}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {open && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                      <button onClick={() => { navigate('/userdashboard'); setOpen(false); }}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-stone-50 transition border-b border-gray-50">
                        {t('my_profile')}
                      </button>
                      <button onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-stone-50 transition">
                        {t('logout')}
                      </button>
                    </div>
                  )}
                </div>
             ) : loading ? null : (
                <button onClick={() => setAuthOpen(true)} className="hdr-cta">
                  {t('login')}
                </button>
              )}
            </div>

            {/* Mobile: Gallery + Hamburger */}
  <div className="lg:hidden flex items-center gap-2 flex-shrink-0 min-w-0">
              <LanguageSwitcher compact />

              <a href="/gallery" aria-label={t('gallery')} className="hdr-ctl hdr-btn">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="3" y="3" width="14" height="14" rx="3.5" />
                  <circle cx="8" cy="8.2" r="1.5" />
                  <path d="M17 12.6l-3.1-3.1a2 2 0 0 0-2.8 0L3.7 16.9" />
                  <path d="M8 21h9a4 4 0 0 0 4-4V8" />
                </svg>
              </a>

              <button
                type="button"
                aria-label={t('menu')}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav"
                className={`hdr-ctl hdr-btn${mobileMenuOpen ? ' is-open' : ''}`}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span className="bar top" />
                <span className="bar mid" />
                <span className="bar bot" />
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div id="mobile-nav" className="lg:hidden bg-white/95 backdrop-blur-md rounded-2xl mb-4 p-4 border border-gray-100 shadow-2xl">
              <div ref={mobileSearchRef} className="relative mb-4">
                <div className="flex items-center gap-2 rounded-full px-4 py-2.5"
                  style={{ background: "rgba(240,246,248,0.95)", border: "1.5px solid var(--color-border)" }}>
                  <Search className="w-4 h-4" style={{ color: "var(--color-ink-400)" }} />
                  <input
                    type="text"
                    placeholder={t('search_salon_specialist')}
                    className="bg-transparent text-sm focus:outline-none flex-1"
                    style={{ color: "var(--color-ink-900)" }}
                    value={mobileSearchQuery}
                    onChange={(e) => setMobileSearchQuery(e.target.value)}
                    onFocus={() => setMobileSearchFocused(true)}
                  />
                </div>
                {renderSearchDropdown({
                  focused: mobileSearchFocused,
                  query: mobileSearchQuery,
                  loading: mobileLoadingSearch,
                  results: mobileSearchResults,
                  onNavigate: (path) => {
                    navigate(path);
                    setMobileSearchFocused(false);
                    setMobileSearchQuery("");
                    setMobileSearchResults(null);
                    setMobileMenuOpen(false);
                  },
                })}
              </div>
              <div className="space-y-1">
                {navLinks.map((link) => (
                  <a key={link.label} href={link.href}
                    className="block px-4 py-2.5 text-sm font-semibold rounded-xl transition"
                    style={{ color: "var(--color-ink-700)" }}>
                    {link.label}
                  </a>
                ))}
               <hr className="my-2" style={{ borderColor: "var(--color-border)" }} />

                {currentUser ? (
                  <>
                    <button onClick={() => { navigate('/userdashboard'); setMobileMenuOpen(false); }}
                      className="block w-full text-left px-4 py-2.5 text-sm font-semibold rounded-xl transition"
                      style={{ color: "var(--color-ink-700)" }}>
                      {t('my_profile')}
                    </button>
                    <button onClick={handleLogout}
                      className="block w-full text-left px-4 py-2.5 text-sm font-semibold rounded-xl transition"
                      style={{ color: "var(--color-ink-700)" }}>
  {t('logout')}
                    </button>
                  </>
                ) : loading ? null : (
                  <button onClick={() => { setAuthOpen(true); setMobileMenuOpen(false); }}
                    className="w-full rounded-xl px-4 py-2.5 text-sm font-bold transition"
                    style={{ background: "var(--color-ink-900)", color: "#fff" }}>
                    {t('login')}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {authOpen && (
        <Suspense fallback={null}>
          <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
        </Suspense>
      )}
    </>
  );
}